import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { PageHeader } from "@/components/data-display/page-header";
import { UnavailableValue } from "@/components/data-display/unavailable-value";
import {
  FilterBar,
  FilterChips,
  FilterSearch,
  FilterSelect,
  matchesAllTerms,
  type FilterChip,
} from "@/components/data-display/filter-bar";
import { Button, buttonVariants } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/feedback";
import { LoadingTable } from "@/components/ui/system-state";
import { TableWrapper } from "@/components/ui/table";
import { WipHint } from "@/components/ui/wip-hint";
import { useInvoiceExceptions, useInvoices } from "@/hooks/use-invoices";
import { createInvoiceExport, duplicateInvoice, NETWORK_ERROR_CODE } from "@/features/invoicing/api";
import { DOCUMENT_STATUS, PAYMENT_STATUS } from "@/features/invoicing/status";
import type { ApiErrorInfo } from "@/lib/api";
import type { DocumentStatus, ExportFilters, PaymentStatus } from "@/features/invoicing/types";
import { ActionRefusal, InvoicingReadFailure } from "./failure";
import { invoiceTableColumnCount, InvoiceTable } from "./invoice-table";
import { ReceivablesSummary, type ReceivablesTile } from "./receivables-summary";
import { INVOICES_LIST_COPY } from "./copy";
import { useInvoicingScope } from "./use-invoicing-scope";

/**
 * `/stablecoins/invoices` — the receivables home (page spec `invoices-list`,
 * design frame INV-00, SPEC-IVC).
 *
 * ## What is real here
 *
 * Two contract reads and two contract writes, and nothing else:
 *
 *   - `GET /v1/invoices` (cursor-paged) through `useInvoices`;
 *   - `GET /v1/invoice-exceptions?state=OPEN` through `useInvoiceExceptions`, which
 *     is the only source of the design frame's **needs-review count** — the
 *     exceptions awaiting a resolution decision. Both routes are the ones the
 *     page spec's `operator_reviews_receivables` action declares.
 *   - `POST /v1/invoices/exports` (audited export) and
 *     `POST /v1/invoices/{id}/duplicate`, both through `features/invoicing/api.ts`,
 *     which mints the `Idempotency-Key` every mutating route in this contract
 *     requires.
 *
 * There is no sample data, no fallback array and no hard-coded invoice, payer or
 * amount anywhere in this module or its children. A refusal from either read
 * empties the rows (`use-invoices.ts` guarantees it), so an error can never
 * leave the previous filter's figures under the new filter's heading.
 *
 * ## The summary row
 *
 * The design spec's rule for it is "every tile names its source and the
 * timestamp it was computed; a source that is unavailable shows unknown, never
 * zero". Two consequences are visible in `buildTiles` below:
 *
 *   - the money tile (**outstanding**) has **no source**: the contract exposes
 *     no receivables total, and a total summed from the loaded rows would be
 *     wrong as well as partial, because one page can mix assets (USDC and USDT
 *     invoices would be added together). It renders the shared unavailable
 *     marker;
 *   - the four count tiles are tallies of a **status field the response
 *     returned** — never a float, never an estimate — and each names its route
 *     and the minute it was computed. When the service says another page
 *     exists, the count is a floor and the tile says so with the same `+` the
 *     transfers list uses, rather than presenting a partial count as a total.
 *
 * ## Search
 *
 * The contract's list route has no free-text parameter (`document_status`,
 * `payment_status`, `payer`, `due_from`, `due_to`, `cursor`, `limit` only), so
 * the search box filters the rows already loaded and the page says so under the
 * field. It is never sent to the service as a filter it does not have.
 *
 * ## Refusals
 *
 * A 403 on either read renders `PermissionDeniedState`; any other failure
 * renders the shared not-available state with the refusal's bounded reason code
 * and its support reference. A refused export or duplicate is shown inline above
 * the list and the rows stay put — the failed write did not invalidate the read.
 * Nothing here retries a step-up or re-issues a refused mutation.
 *
 * ## Permissions
 *
 * Export is offered only to a session whose loaded contract grants
 * `invoice:export`; duplicate only with `invoice:create`. Both read the scope
 * from `GET /v1/session/effective-access` and fail closed (`use-invoicing-scope.ts`).
 * The service still decides; this only stops the page from offering an action
 * the contract says the actor must be denied.
 */

/** The "no filter" sentinel, kept out of the wire filter object. */
const ALL = "all";

function clock(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** The rows the client-side search matches, over the fields the row projection carries. */
function matchesSearch(
  invoice: { invoiceId: string; number: string | null; payerId: string; assetSymbol: string; documentStatus: string; paymentStatus: string },
  term: string,
): boolean {
  return matchesAllTerms(term, [
    invoice.number,
    invoice.invoiceId,
    invoice.payerId,
    invoice.assetSymbol,
    invoice.documentStatus,
    invoice.paymentStatus,
  ]);
}

type ExportState =
  | { kind: "idle" }
  | { kind: "pending" }
  | { kind: "queued"; exportId: string }
  | { kind: "refused"; error: ApiErrorInfo };

type DuplicateState =
  | { kind: "idle" }
  | { kind: "pending"; invoiceId: string }
  | { kind: "created"; invoiceId: string; number: string | null; sourceLabel: string }
  | { kind: "refused"; error: ApiErrorInfo };

export function InvoicesList() {
  const copy = INVOICES_LIST_COPY;
  const [documentStatus, setDocumentStatus] = useState<string>(ALL);
  const [paymentStatus, setPaymentStatus] = useState<string>(ALL);
  const [query, setQuery] = useState("");

  const canExport = useInvoicingScope("invoice:export");
  const canCreate = useInvoicingScope("invoice:create");
  const canDuplicate = canCreate;

  const invoices = useInvoices({
    documentStatus: documentStatus === ALL ? undefined : (documentStatus as DocumentStatus),
    paymentStatus: paymentStatus === ALL ? undefined : (paymentStatus as PaymentStatus),
  });
  // The needs-review count is the open-exception count; see the module header.
  const exceptions = useInvoiceExceptions({ state: "OPEN" });

  const { data: rows, loading, error, refresh, hasMore, loadMore, loadingMore, loadMoreError } = invoices;

  /**
   * When the tiles were computed. Recorded when a read settles rather than read
   * from the clock during render, so the timestamp names the moment the data
   * arrived — the same claim the design spec makes the source line carry.
   */
  const [summaryAt, setSummaryAt] = useState<Date | null>(null);
  useEffect(() => {
    if (!loading && !error && rows.length > 0) setSummaryAt(new Date());
  }, [loading, error, rows]);

  const term = query.trim();
  const searchActive = term !== "";
  const filtersActive = documentStatus !== ALL || paymentStatus !== ALL || searchActive;

  const visible = useMemo(
    () => (searchActive ? rows.filter((invoice) => matchesSearch(invoice, term)) : rows),
    [rows, searchActive, term],
  );

  const buildTiles = (): ReceivablesTile[] => {
    const countWith = (status: PaymentStatus) => rows.filter((row) => row.paymentStatus === status).length;
    const at = summaryAt ? clock(summaryAt) : null;
    // A count over the loaded page is a floor while the service holds another
    // page; a group with nothing loaded reads its plain count, never "0+".
    const lower = (count: number) => (hasMore && count > 0 ? { lowerBound: true, lowerBoundTitle: copy.summary.atLeast(count) } : {});
    const tile = (
      key: string,
      label: string,
      count: number,
      tone: ReceivablesTile["tone"],
      source: string,
      computedAt: string | null,
    ): ReceivablesTile => ({ key, label, value: count, tone, source, computedAt, ...lower(count) });

    const exceptionTile = (): ReceivablesTile => {
      if (exceptions.loading) {
        return {
          key: "needs-review",
          label: copy.summary.label.needsReview,
          value: <UnavailableValue label={copy.summary.needsReviewLoading} />,
          source: copy.summary.exceptionSource,
          computedAt: null,
        };
      }
      // A source that failed shows unknown, never zero (INV-00).
      if (exceptions.error) {
        return {
          key: "needs-review",
          label: copy.summary.label.needsReview,
          value: <UnavailableValue label={copy.summary.needsReviewUnavailable} />,
          source: copy.summary.needsReviewSourceUnavailable,
          computedAt: null,
        };
      }
      const count = exceptions.data.length;
      return {
        key: "needs-review",
        label: copy.summary.label.needsReview,
        value: count,
        tone: "warning",
        source: copy.summary.exceptionSource,
        computedAt: at,
        ...(exceptions.hasMore && count > 0
          ? { lowerBound: true, lowerBoundTitle: copy.summary.atLeast(count) }
          : {}),
      };
    };

    return [
      {
        key: "outstanding",
        label: copy.summary.label.outstanding,
        value: <UnavailableValue label={copy.summary.outstandingUnavailable} />,
        source: copy.summary.outstandingSource,
        computedAt: null,
      },
      tile("overdue", copy.summary.label.overdue, countWith("overdue"), "danger", copy.summary.invoiceSource, at),
      tile("confirming", copy.summary.label.confirming, countWith("confirming"), "pending", copy.summary.invoiceSource, at),
      tile("under-review", copy.summary.label.underReview, countWith("under_review"), "warning", copy.summary.invoiceSource, at),
      exceptionTile(),
    ];
  };

  const createLink = canCreate ? (
    <Link to="/stablecoins/invoices/new" className={buttonVariants({ size: "sm" })}>
      {copy.create.action}
    </Link>
  ) : undefined;

  /* ------------------------------------------------------------- actions */

  const [exportState, setExportState] = useState<ExportState>({ kind: "idle" });
  const [duplicateState, setDuplicateState] = useState<DuplicateState>({ kind: "idle" });

  const exportFilters = useMemo(() => {
    const filters: ExportFilters = {};
    if (documentStatus !== ALL) filters.document_status = documentStatus as DocumentStatus;
    if (paymentStatus !== ALL) filters.payment_status = paymentStatus as PaymentStatus;
    return filters;
  }, [documentStatus, paymentStatus]);

  const runExport = useCallback(async () => {
    setExportState({ kind: "pending" });
    try {
      const result = await createInvoiceExport({ filters: exportFilters });
      if (result.ok) setExportState({ kind: "queued", exportId: result.data.exportId });
      else setExportState({ kind: "refused", error: result.error });
    } catch {
      setExportState({ kind: "refused", error: { reasonCode: NETWORK_ERROR_CODE } });
    }
  }, [exportFilters]);

  const runDuplicate = useCallback(
    async (invoiceId: string, label: string) => {
      setDuplicateState({ kind: "pending", invoiceId });
      try {
        const result = await duplicateInvoice(invoiceId);
        if (result.ok) {
          setDuplicateState({ kind: "created", invoiceId: result.data.invoiceId, number: result.data.number, sourceLabel: label });
          // The new draft belongs in the list; re-read page 1 under the current filters.
          refresh();
        } else {
          setDuplicateState({ kind: "refused", error: result.error });
        }
      } catch {
        setDuplicateState({ kind: "refused", error: { reasonCode: NETWORK_ERROR_CODE } });
      }
    },
    [refresh],
  );

  const clearAll = () => {
    setDocumentStatus(ALL);
    setPaymentStatus(ALL);
    setQuery("");
  };

  const chips: FilterChip[] = [];
  if (documentStatus !== ALL) {
    chips.push({
      key: "document",
      label: `${copy.filters.chip.documentStatus}: ${DOCUMENT_STATUS[documentStatus as DocumentStatus]?.label ?? documentStatus}`,
      clear: () => setDocumentStatus(ALL),
    });
  }
  if (paymentStatus !== ALL) {
    chips.push({
      key: "payment",
      label: `${copy.filters.chip.paymentStatus}: ${PAYMENT_STATUS[paymentStatus as PaymentStatus]?.label ?? paymentStatus}`,
      clear: () => setPaymentStatus(ALL),
    });
  }
  if (searchActive) {
    chips.push({ key: "q", label: `${copy.filters.chip.search}: “${term}”`, clear: () => setQuery("") });
  }

  const ready = !loading && !error;
  // Nothing exists yet and nothing is narrowing the list: the create-first-invoice
  // state, not a filter miss.
  const firstRun = ready && rows.length === 0 && !filtersActive && !hasMore;

  return (
    <div>
      <PageHeader
        title={copy.page.title}
        subtitle={copy.page.subtitle}
        actions={
          <>
            <WipHint />
            {createLink}
            {canExport && (
              <Button
                variant="secondary"
                size="sm"
                disabled={exportState.kind === "pending" || loading || !!error}
                onClick={() => void runExport()}
              >
                {exportState.kind === "pending" ? copy.export.pending : copy.export.action}
              </Button>
            )}
          </>
        }
      />

      {exportState.kind === "queued" && (
        <p role="status" className="mb-4 rounded-lg border border-line bg-surface px-4 py-2.5 text-sm text-muted">
          {copy.export.queued(exportState.exportId)}
        </p>
      )}
      {exportState.kind === "refused" && (
        <ActionRefusal
          title={copy.export.refusedTitle}
          error={exportState.error}
          onDismiss={() => setExportState({ kind: "idle" })}
        />
      )}
      {duplicateState.kind === "created" && (
        <p role="status" className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-line bg-surface px-4 py-2.5 text-sm text-muted">
          <span>{copy.duplicate.created(duplicateState.number, duplicateState.sourceLabel)}</span>
          <Link
            to={`/stablecoins/invoices/view?id=${encodeURIComponent(duplicateState.invoiceId)}`}
            className="font-semibold text-primary hover:underline"
          >
            {copy.duplicate.openDraft}
          </Link>
        </p>
      )}
      {duplicateState.kind === "refused" && (
        <ActionRefusal
          title={copy.duplicate.refusedTitle}
          error={duplicateState.error}
          onDismiss={() => setDuplicateState({ kind: "idle" })}
        />
      )}

      {/* The tiles are a view over the page the service returned. They are not
          rendered while the read is failing or still in flight: a row of zeros
          during loading is a fabricated count. */}
      {ready && !firstRun && (
        <>
          <ReceivablesSummary tiles={buildTiles()} />
          {hasMore && <p className="mb-4 text-xs text-muted">{copy.summary.scopeNote}</p>}
        </>
      )}

      <FilterBar>
        <FilterSearch
          value={query}
          onChange={setQuery}
          placeholder={copy.filters.searchPlaceholder}
          ariaLabel={copy.filters.searchAriaLabel}
          className="max-w-[300px]"
        />
        <FilterSelect
          value={documentStatus}
          onChange={setDocumentStatus}
          ariaLabel={copy.filters.documentStatusAriaLabel}
          options={[
            { value: ALL, label: copy.filters.allDocumentStatuses },
            ...Object.entries(DOCUMENT_STATUS).map(([value, presentation]) => ({ value, label: presentation.label })),
          ]}
        />
        <FilterSelect
          value={paymentStatus}
          onChange={setPaymentStatus}
          ariaLabel={copy.filters.paymentStatusAriaLabel}
          options={[
            { value: ALL, label: copy.filters.allPaymentStatuses },
            ...Object.entries(PAYMENT_STATUS).map(([value, presentation]) => ({ value, label: presentation.label })),
          ]}
        />
        <button
          type="button"
          onClick={refresh}
          aria-label="Refresh invoices"
          className="inline-flex items-center gap-1.5 rounded-md border border-line bg-surface px-2.5 py-1.5 text-xs font-medium text-muted hover:text-ink"
        >
          <RefreshCw className="size-3.5" aria-hidden />
        </button>
      </FilterBar>

      <FilterChips chips={chips} onClearAll={clearAll} testId="invoice-filter-chips" />

      {searchActive && hasMore && <p className="mb-3 text-xs text-muted">{copy.filters.searchHint}</p>}

      {loading && <LoadingTable rows={6} cols={invoiceTableColumnCount(canDuplicate)} label={copy.loadingTable} />}

      {!loading && error && <InvoicingReadFailure error={error} subject="the receivables list" onRetry={refresh} />}

      {firstRun && (
        <EmptyState card title={copy.empty.title} description={copy.empty.description} action={createLink} />
      )}

      {ready && !firstRun && (
        <TableWrapper>
          <InvoiceTable
            invoices={visible}
            canDuplicate={canDuplicate}
            duplicatePendingId={duplicateState.kind === "pending" ? duplicateState.invoiceId : null}
            onDuplicate={(invoiceId, label) => void runDuplicate(invoiceId, label)}
          />
          {visible.length === 0 && (
            <EmptyState
              title={copy.empty.filteredTitle}
              description={copy.empty.filteredDescription}
              action={
                <Button variant="secondary" size="sm" onClick={clearAll}>
                  {copy.empty.clearFilters}
                </Button>
              }
            />
          )}
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-4 py-3">
            <span className="text-xs text-muted" data-testid="invoice-list-count">
              {copy.paging.count(visible.length, rows.length)}
              {hasMore && ` · ${copy.paging.moreAvailable}`}
            </span>
            {hasMore && (
              <Button variant="secondary" size="sm" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? copy.paging.loadingMore : copy.paging.loadMore}
              </Button>
            )}
          </div>
          {loadMoreError && (
            <div className="border-t border-line px-4 py-3">
              <InvoicingReadFailure error={loadMoreError} subject="more invoices" onRetry={loadMore} />
            </div>
          )}
        </TableWrapper>
      )}
    </div>
  );
}
